import {ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {ErrorType, Scilearn} from "../constants";
import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import {ActivityEventData, ActivityModalEvent} from "../event/ActivityEvent";
import {RecordingService, RecordingServiceStatus} from "../services/recording/recording.service";
import {MICROPHONECHECK_STATUS, MicrophoneCheckEvent} from "../event/MicrophoneCheckEvent";
import {PauseSelectionEvent} from "../event/ReadAndRecordEvents";
import {MicrophoneCheckDataService} from "../services/recognition/microphone-check-data.service";
import {CssSentence, CssWord} from "../content/content-view.objects";
import {ComponentBase} from "../base/ComponentBase";
import {ClientConfiguration} from "../services/configuration/client.config";
import {BrowserAgent} from "../services/browser/browser.agent";
import {MicrophoneService, MicrophoneStatus} from "../services/audio/microphone.service";
import {RecognizerService} from "../services/recognition/recognizer.service";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {TranslateService} from "@ngx-translate/core";
import {MessageService} from "../services/notification/message.service";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {AudioQueueItem, TranslateAudio} from "../services/audio-playback/audioqueue.item";
import {AudioQueueEvent, InterruptAudio} from "../event/AudioQueueEvent";
import {GAEventService} from "../services/analytics/GAEventServce";
import {Observable} from "rxjs";
import {filter} from "rxjs/operators";

export enum MicrophoneCheckStep {
    MICROPHONE_REQUESTED = 0,
    MICROPHONE_CHECK = 1,
    NUMBER_TEST = 2,
    COMPLETED = 3,
    DISCONNECTED = 4,
    NUMBER_TEST_ERROR= 5
}

@Component({
    selector: "microphone-check-modal",
    templateUrl: "./microphone.check.modal.component.html",
    styleUrls: [ "./microphone.check.component.scss"]
})
export class MicrophoneCheckModalComponent extends ComponentBase implements OnInit {

    public currentStep:number = MicrophoneCheckStep.MICROPHONE_REQUESTED;
    private modalType: string = Scilearn.MICROPHONE_CHECK_MODAL;
    public activateModal: boolean = false;
    public title:string;
    private currentContent:CssSentence = null;
    public contentMessage:string;
    public errorText:String;
    public volumeProgress:number = 0;
    private numberAttempts:number = 0;
    public subMessage1:string;
    public subMessage2:string;
    public subMessage3:string;
    public microphoneCheckStep = MicrophoneCheckStep; // so we can access the enum in the html
    public browser:string;
    public message: string;
    private microphoneStatus:MicrophoneStatus;
    public volumeBar : { volume: boolean }[] = Array.from({ length: 31 } , () => ({ volume: false }));

    constructor(
        private microphoneCheckDataService:MicrophoneCheckDataService,
        private recognizerService:RecognizerService,
        private microphoneService:MicrophoneService,
        private audioQueueService: AudioQueueService,
        public recordingService:RecordingService,
        private changeDetectorRef: ChangeDetectorRef,
        private log:BugsnagLoggingService,
        private eventBus: EventBus,
        private modalConfig: ModalConfig,
        private clientConfig: ClientConfiguration,
        private browserAgent: BrowserAgent,
        private translateService: TranslateService,
        private messageService: MessageService,
        private gaEventService: GAEventService) {

        super("MicrophoneCheckModalComponent");
    }

    ngOnInit(): void {
        this.log.log("initializing Microphone Check Component");
        this.registerObservers();
        this.determineBrowser();
    }

    public verifyMicrophonePermissionsSetup():void {

        // they denied using the microphone or it is still blocked from a previous denial
        // TODO: is this used? if not want to remove since we dont have text in json
        if (this.isBadMicrophoneStatus()) {
            this.errorText = "Please allow your microphone to be used.";
        } else {
            this.setMicrophoneCheckState();
        }
    }

    public setNumbersTestCheck(withGesture: boolean): void {
        this.audioQueueService.reset().subscribe(() => {
            this.makeSureMicWorks(withGesture).subscribe((success) => {
                this.log.log("makeSureMicWorks success: " + success);
                this.currentStep = MicrophoneCheckStep.NUMBER_TEST;
                this.errorText = null;
                this.currentContent = this.getNumbersSentence();
                this.eventBus.next(
                    new AudioQueueEvent(
                        [new AudioQueueItem("RA_MIC_05", TranslateAudio.YES, () => this.runNumbersTest())
                        ], InterruptAudio.YES));
                this.translateService.get("RA_MIC_05", TranslateAudio.YES).subscribe((res) => {
                    this.title = res;
                });
                this.volumeProgress = 0;
            });
            this.volumeBar.forEach(vol => {
                vol.volume = false;
            })
        });
    }

    private makeSureMicWorks(withGesture: boolean): Observable<boolean> {
        return Observable.create(observer => {
            if (this.microphoneService.isSafari() && withGesture) {
                this.recognizerService.annoyingGesture();
                this.tryUserMedia().subscribe((success) => {
                    this.log.log("first getUserMedia try success: " + success);
                    if (success) {
                        observer.next(success);
                        observer.complete();
                    } else {
                        this.tryUserMedia().subscribe((secondTrySuccess) => {
                            observer.next(secondTrySuccess);
                            observer.complete();
                            this.log.log("second getUserMedia try success: " + secondTrySuccess);
                        });
                    }
                });
            } else {
                observer.next(true);
                observer.complete();
            }
        });
    }

    public runNumbersTest():void {
        this.changeDetectorRef.detectChanges();
        this.changeDetectorRef.detach();
        this.recordingService.startMicrophoneCheck(this.currentContent, this.onVolumeCallback);
    }

    public cancel(sendEvent?: boolean): void {
        this.closeModal();
        this.eventBus.next(new PauseSelectionEvent()); // need to automatically trigger pause
        if (sendEvent) {
            this.eventBus.next(new MicrophoneCheckEvent(MICROPHONECHECK_STATUS.cancel)); // needed for word wall
        }
    }

    public closeModal(): void {
        this.audioQueueService.hardReset();
        this.currentStep = MicrophoneCheckStep.MICROPHONE_REQUESTED;
        this.currentContent = null;
        this.numberAttempts = 0;
        this.activateModal = false;
        this.modalConfig.removeModal(new ActivityEventData({type: this.modalType}));
    }

    private testUserMedia(withGesture: boolean):Observable<boolean> {
        let millisecondsToRecord = 500; // this is for debugging purposes so want a good chunk of signal
        return Observable.create(observer => {
            if (this.microphoneService.isSafari() && withGesture) {
                this.microphoneService.startAudioOnly();
                setTimeout(() => {
                    this.microphoneService.stopAudioOnly();
                    observer.next(this.microphoneStatus === MicrophoneStatus.READY);
                    observer.complete();
                }, millisecondsToRecord);
            } else {
                observer.next(true);
                observer.complete();
            }
        });
    }

    private tryUserMedia(): Observable<boolean> {
        let millisecondsToRecord = 200; // 200 milliseconds is good tradeoff point between making sure we record
        // enough to be confident of the result, and not introducing too noticeable a delay for the user.
        return Observable.create(observer => {
            this.microphoneService.startAudioOnly();
            setTimeout(() => {
                this.microphoneService.stopAudioOnly();
                if (this.microphoneStatus === MicrophoneStatus.READY) {
                    observer.next(true);
                    observer.complete();
                } else {
                    // if the mic is not ready (usually due to MicrophoneStatus.SILENT), kick it....
                    this.log.log("check failed due to status:" + this.microphoneStatus);
                    this.microphoneService.safariRebootGetUserMedia().subscribe((rebootSuccess) => {
                        observer.next(false);
                        observer.complete();
                    });
                }
            }, millisecondsToRecord);

        });
    }

    private registerObservers():void {
        let microphoneCheckEvent = this.eventBus.pipe(filter(
            (e:MicrophoneCheckEvent) => e.type === MicrophoneCheckEvent.TYPE)).subscribe((e:MicrophoneCheckEvent) => {
                this.log.log("Received a microphone check event with status "
                             + MICROPHONECHECK_STATUS[e.status].toString());
                this.initialize(e.status);
            });

        let recordingServiceStatusSubscription = this.recordingService.recordingServiceStatus.subscribe((event) => {
            switch (event) {
                case RecordingServiceStatus.MICROPHONE_CHECK_REQUESTED:
                    this.log.log("Requesting microphone.");
                    this.setMicrophonePermissionsState();
                    break;
                case RecordingServiceStatus.MICROPHONE_CHECK_READY:
                    this.log.log("Microphone is supposedly ready.");
                    // Sometimes we get a microphone check ready message when the microphone has not been allowed by the
                    // user. If it has been blocked, then this catches it.  But it the user didn't accept or block, I
                    // don't know how to tell.  I would like to create a method on the microphone-service class that
                    // we could say do we have microphone access?  All I know here is that it was/was-not denied.
                    if (this.isBadMicrophoneStatus()) {
                        this.setMicrophoneCheckRequestState();
                    } else {
                        this.setMicrophoneCheckState();
                    }
                    break;
                case RecordingServiceStatus.MICROPHONE_CHECK_SENTENCE_COMPLETED:
                    this.changeDetectorRef.reattach();
                    this.changeDetectorRef.detectChanges();
                    this.setCompletedState();
                    break;
                case RecordingServiceStatus.MICROPHONE_CHECK_SENTENCE_TIMEOUT:
                    this.changeDetectorRef.reattach();
                    this.changeDetectorRef.detectChanges();
                    if (this.isNumberTest() && this.isFirstAttempt()) {
                        this.handleFirstMicTestFailure();
                    } else if (this.isNumberTest()) {
                        this.handleSecondMicTestFailure();
                    }
                    break;
                default:
                    this.log.log("Not handling recording service event " + event);
                    break;
            }
        });

        let microphoneStatusSubscription = this.recognizerService.microphoneStatusObservable.subscribe(status => {
            this.log.log("Updating microphone status=" + MicrophoneStatus[status]);
            this.microphoneStatus = status;
            if ((this.microphoneStatus === MicrophoneStatus.AUDIO_NOT_AVAILABLE) && this.activateModal) {
                this.log.log("Cancelling modal, no microphone available");
                this.cancel(false);
            }
        });

        let messageEvent = this.eventBus.pipe(filter(event => event instanceof ActivityModalEvent))
            .subscribe((event: ActivityModalEvent) => {
            if (!!event.data.message && this.activateModal) {
                this.closeModal();
            }
        });

        this.subscriptions.push(messageEvent);
        this.subscriptions.push(microphoneCheckEvent);
        this.subscriptions.push(recordingServiceStatusSubscription);
        this.subscriptions.push(microphoneStatusSubscription);
    }

    private isNumberTest(): boolean {
        return MicrophoneCheckStep.NUMBER_TEST === this.currentStep;
    }

    private isFirstAttempt(): boolean {
        return this.numberAttempts === 0;
    }

    private handleFirstMicTestFailure(): void {
        let messageObj = this.messageService.getMessage("non_speech");
        this.translateService.get(messageObj.text2).subscribe((res) => {
            this.errorText = res[messageObj.text2];
            this.eventBus.next(
                new AudioQueueEvent([new AudioQueueItem(messageObj.audio, TranslateAudio.YES)],
                                    InterruptAudio.YES));
        });
        this.numberAttempts++;
        if (this.microphoneService.isSafari() && this.microphoneStatus === MicrophoneStatus.SILENT) {
            this.log.error("Safari/iOS: failed microphone check due to MicrophoneStatus.SILENT", ErrorType.MICROPHONE);
            this.microphoneService.safariRebootGetUserMedia();
        }
    }

    private handleSecondMicTestFailure(): void {
        this.currentStep = MicrophoneCheckStep.NUMBER_TEST_ERROR;
        this.activateModal = true;
        let messageObj = this.messageService.getMessage("non_speech_final");
        let keys = [];

        keys.push(messageObj.text1);
        keys = keys.concat(this.messageService.splitMessages(messageObj.text2));

        this.translateService.get(keys).subscribe((res) => {
            this.title = res[messageObj.text1];
            this.message = res.CANT_HEAR_CHECK_MIC;
            this.subMessage1 = res.CHECK_MIC_USB_ADAPTER;
            this.subMessage2 = res.CHECK_MIC_MUTE;
            this.subMessage3 = res.POSITION_MIC;
        });
        this.eventBus.next(
            new AudioQueueEvent([new AudioQueueItem(messageObj.audio, TranslateAudio.YES)], InterruptAudio.YES));
        if (this.microphoneService.isSafari() && this.microphoneStatus === MicrophoneStatus.SILENT) {
            this.microphoneService.safariRebootGetUserMedia();
        }
        this.gaEventService.emitEvent("error", "micophone", "handleSecondMicTestFailure", 1);
        this.log.error("Microphone check error modal: non_speech_final", ErrorType.MICROPHONE);
    }

    private determineBrowser(): void {
        this.browser = this.browserAgent.getBrowserName();
        this.log.log("Browser=" + this.browser);
    }

    private initialize(status:MICROPHONECHECK_STATUS): void {
        this.log.log("Calling initialize with status " + MICROPHONECHECK_STATUS[status].toString());
        switch (status) {
            case MICROPHONECHECK_STATUS.connect:
                if (this.currentStep === MicrophoneCheckStep.DISCONNECTED) {
                    this.log.log("Closing modal because status=connect and step=disconnected.");
                    this.closeModal();
                }
                break;
            case MICROPHONECHECK_STATUS.disconnect:
                this.log.log("got disconnect event current step: " + this.currentStep);
                if (this.currentStep !== MicrophoneCheckStep.DISCONNECTED) {
                    this.log.log("Closing modal because status=disconnected and step!=disconnected.");
                    this.cancel(false);
                }
                break;
            case MICROPHONECHECK_STATUS.reset:
                this.log.log("got reset event current step: " + this.currentStep);
                this.recordingService.microphoneCheckCompleted = false;
                break;
            case MICROPHONECHECK_STATUS.manual:
            case MICROPHONECHECK_STATUS.automatic:
                let sub = this.recordingService.initPronunciationDictionary(
                    this.microphoneCheckDataService.getMicDictionary()).subscribe((success) => {
                    this.recordingService.requestMicrophone();
                });
                this.subscriptions.push(sub);
                break;
            default:
                break;
        }
    }

    private isBadMicrophoneStatus(): boolean {
        return this.microphoneStatus && this.microphoneStatus === MicrophoneStatus.MIC_DENIED;
    }

    private setMicrophonePermissionsState():void {
        if (window["cordova"]) {
            window["cordova"].exec(
                (data) => {
                    this.log.log("check microphone permission" + JSON.stringify(data));
                    this.setMicrophoneCheckRequestState();
                },
                (err) => {
                    this.log.log("check microphone issue " + err);
                },
                "Microphone",
                "checkMicrophone",
                []
            );
        } else {
            this.setMicrophoneCheckRequestState();
        }
    }

    private setMicrophoneCheckRequestState(): void {
        this.currentStep = MicrophoneCheckStep.MICROPHONE_REQUESTED;

        if (this.browser === "Safari") {
            this.translateService.get(["RA_MIC_03", "LETS_SETUP_MICROPHONE"]).subscribe((res) => {
                this.title = res.LETS_SETUP_MICROPHONE;
                this.message = res.RA_MIC_03;
            });
        } else {
            this.activateModal = true;
            this.translateService.get(["ACCESS_MIC_CHROME", "LETS_SETUP_MICROPHONE"])
                .subscribe((res) => {
                    this.title = res.LETS_SETUP_MICROPHONE;
                    this.message = res.ACCESS_MIC_CHROME;
                });
        }
    }

    private setMicrophoneCheckState(): void {
        this.audioQueueService.reset().subscribe(() => {
            this.currentStep = MicrophoneCheckStep.MICROPHONE_CHECK;

            this.activateModal = true;
            this.translateService.get(["RA_MIC_03", "LETS_SETUP_MICROPHONE"]).subscribe((res) => {
                this.title = res.LETS_SETUP_MICROPHONE;
                this.message = res.RA_MIC_03;
                this.eventBus.next(
                    new AudioQueueEvent([new AudioQueueItem("RA_MIC_03", TranslateAudio.YES)],
                                        InterruptAudio.YES));
            });
        });
    }

    private setCompletedState(): void {
        this.currentStep = MicrophoneCheckStep.COMPLETED;
        let messageObj = this.messageService.getMessage("check_complete_manual");
        let keys = [];

        keys.push(messageObj.text1);
        keys.push(messageObj.text2);

        this.translateService.get(keys).subscribe((res) => {
            this.title = res[messageObj.text1];
            this.contentMessage = res[messageObj.text2];
            this.activateModal = true;
            this.eventBus.next(
                new AudioQueueEvent(
                    [new AudioQueueItem(messageObj.audio, TranslateAudio.YES)], InterruptAudio.YES));
        });

    }

    public endMicrophoneCheck():void {
        this.audioQueueService.reset().subscribe(() => {
            this.log.log("Terminating microphone check.");
            this.closeModal();
            this.eventBus.next(new MicrophoneCheckEvent(MICROPHONECHECK_STATUS.success));
        });
    }

    private getNumbersSentence():CssSentence {
        let sentence:CssSentence = new CssSentence({words: [], audio: ""}, null);
        this.microphoneCheckDataService.getGrammarCountToTen().words.forEach((word) => {
            sentence.words.push(new CssWord(
                {word: word, category: "2", audio: "", separator: " ", prefix: ""}, sentence));
        });
        return sentence;
    }

    onVolumeCallback = (result) => {
        let MAX_AMPLITUDE = 32767;

        // get percentage of the MAX_AMPLITUDE that the user's voice hit
        let amplitude_ratio = (result * 1) / MAX_AMPLITUDE;
        let volumeBarRatio = Math.round(this.volumeBar.length * amplitude_ratio);
        this.volumeBar.forEach((obj,index) => {
            obj.volume = (index + 1) <= volumeBarRatio ? true : false
       });

        this.changeDetectorRef.reattach();
        this.changeDetectorRef.detectChanges();
        this.changeDetectorRef.detach();
    }

}
